// The day model behind امروز — pure, React-free, text-free.
//
// It is separate from the screen for the reason the doctor app's
// Reception.shared.js gives: who is next, what the queue order is and what the
// day's counts are must be ONE computation, or two surfaces (and the tests) end
// up disagreeing about which patient walks in next.
//
// The status vocabulary here is the SCHEDULING service's, verbatim —
// scheduled | arrived | in_visit | completed | cancelled — because that is what
// parsina.bff.dentist exposes and it deliberately carries no second vocabulary.

import { iranClockOf, iranMinutesOf } from './iranTime.js';

export const APPOINTMENT_STATUSES = [
  'scheduled', 'arrived', 'in_visit', 'completed', 'cancelled',
];

export const isDone = (s) => s === 'completed' || s === 'cancelled';
export const isHere = (s) => s === 'arrived' || s === 'in_visit';

/** A patient kept waiting past this reads as a problem, not a queue. */
export const WAIT_WARN_MIN = 20;

/**
 * Past this, a transition stamp is not a live clock — it is a stale row.
 *
 * `get_queue_status` filters on status alone, with no date bound, so a patient
 * marked arrived weeks ago and never completed is still in the room list today,
 * and rendering `now − arrivedAt` for that row printed «۴۰٬۳۹۸ دقیقه انتظار» on
 * the day sheet. No clinic keeps a patient waiting (or on the unit) 12 hours;
 * past this bound the honest answer is "there is no wait to measure", the same
 * null an unstamped transition gets. Deliberately elapsed-based rather than
 * calendar-day-based: an evening queue crossing Iran midnight is a real
 * 20-minute wait, not a stale row.
 */
export const STALE_STAMP_MIN = 12 * 60;

/**
 * Add the two display facts every row needs from its epoch: the Iran wall clock
 * and the minutes-past-midnight the ordering uses.
 *
 * The BFF sends `dateTime` only. Deriving `time` here rather than server-side
 * keeps ONE definition of "what o'clock is this appointment" in the client, and
 * it is the same instant the date navigator is showing.
 */
export function decorate(appt) {
  return {
    ...appt,
    time: iranClockOf(appt?.dateTime),
    minutes: iranMinutesOf(appt?.dateTime),
  };
}

/** The name to show for a row. A «ثبت سریع» walk-in has a label and no record. */
export function displayName(appt) {
  return appt?.patient?.name || appt?.placeholderLabel || '';
}

/**
 * Minutes the patient has been sitting in the waiting room.
 * null when the desk never marked them arrived — there is no wait to measure,
 * only a booked slot time. (An `arrivedAt` of null means the transition never
 * happened; the BFF already turned the wire's 0 into null so it cannot read as
 * the Unix epoch.)
 */
export function waitedMinutes(appt, nowMs) {
  const arrived = Number(appt?.arrivedAt);
  if (!Number.isFinite(arrived) || arrived <= 0) return null;
  const min = Math.max(0, Math.floor((nowMs - arrived) / 60000));
  return min > STALE_STAMP_MIN ? null : min;
}

/**
 * How far an open visit has run past its booked slot.
 *   elapsed ≤ duration      → normal
 *   duration < elapsed ≤ 2× → 'over'
 *   2× < elapsed            → 'critical'
 */
export function visitOverrun(appt, nowMs) {
  const started = Number(appt?.visitStartedAt);
  if (!Number.isFinite(started) || started <= 0) return { elapsedMin: null, level: 'normal' };
  const elapsedMin = Math.max(0, Math.floor((nowMs - started) / 60000));
  if (elapsedMin > STALE_STAMP_MIN) return { elapsedMin: null, level: 'normal' };
  const dur = appt.durationMinutes || 30;
  const level = elapsedMin > 2 * dur ? 'critical' : elapsedMin > dur ? 'over' : 'normal';
  return { elapsedMin, level };
}

/**
 * The selected day, derived once.
 *
 * `queue` is passed IN rather than computed here: it comes from the BFF's
 * `queue` query, which passes the scheduling service's own ordering through
 * untouched (in_visit first, then the desk's manual position, then time).
 * Re-deriving that order in the client is how the dentist and the desk end up
 * disagreeing about who walks in next — the doctor app mirrors the sort by hand
 * and carries a comment admitting the risk.
 *
 * Off today the room queue is meaningless (nobody is standing in a waiting room
 * on Thursday), so it is dropped and every non-finished row is "upcoming".
 */
export function buildDayModel({ appointments = [], queue = [], isToday, nowMin }) {
  const dayApts = [...appointments]
    .map(decorate)
    .sort((a, b) => Number(a.dateTime || 0) - Number(b.dateTime || 0));

  const roomQueue = isToday ? queue.map(decorate) : [];
  const queueIds = new Set(roomQueue.map((a) => a.id));

  const upcoming = dayApts.filter((a) => !queueIds.has(a.id) && !isDone(a.status));
  const done = dayApts.filter((a) => isDone(a.status));

  // Rows that are in the ROOM but not on the day sheet. This is not a corner
  // case invented here — two live sources produce it:
  //
  //   * `appointments.clinic_id` is NULLABLE (scheduling/app/infrastructure/db/
  //     models.py:22). `today` filters `clinic_id == clinicId` server-side, so
  //     an unattributed row is dropped from the sheet, while `queue` KEEPS it
  //     on purpose — a patient physically in the room must not vanish.
  //   * `get_queue_status` filters on status alone, with no date bound, so a
  //     patient marked arrived yesterday and never completed is still in the
  //     room list today.
  //
  // They are the reason the screen must not gate the waiting room on the day
  // sheet being non-empty, and the reason the KPI strip counts `counted` and
  // not `dayApts`: «حاضر ۰» above a queue block listing a patient is the
  // screen contradicting itself.
  const dayIds = new Set(dayApts.map((a) => a.id));
  const roomOnly = roomQueue.filter((a) => !dayIds.has(a.id));
  const counted = roomOnly.length > 0 ? [...dayApts, ...roomOnly] : dayApts;

  // Waiting-line position counts only the patients still waiting; the one
  // already in the chair is not "number 1 in line", they are in the chair.
  const queuePosOf = new Map(
    roomQueue.filter((a) => a.status === 'arrived').map((a, i) => [a.id, i + 1]),
  );

  const nextApt = isToday
    ? roomQueue[0] || upcoming.find((a) => a.minutes != null && a.minutes >= nowMin) || null
    : null;

  return { dayApts, roomQueue, roomOnly, counted, queueIds, upcoming, done, queuePosOf, nextApt };
}

/**
 * One count source for the KPI strip — derived from the SAME list the rows
 * render, so the numbers can never disagree with what is underneath them.
 */
export function dayCounts(dayApts) {
  return {
    total: dayApts.length,
    here: dayApts.filter((a) => isHere(a.status)).length,
    scheduled: dayApts.filter((a) => a.status === 'scheduled').length,
    completed: dayApts.filter((a) => a.status === 'completed').length,
    cancelled: dayApts.filter((a) => a.status === 'cancelled').length,
  };
}
